import { useNodeId } from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  createNewTableAtom_SUSPENDS,
  shouldCreateNewTableAtom,
  updateNodeSetter,
  tablesAtom_SUSPENDS,
  saveCanvasSetter,
  closeSecondaryToolbarMenuSetter,
  moveAssetToCanvasFolderSetter,
} from "@zapier/canvas-state";
import { Suspense, useCallback, useEffect, useRef } from "react";
import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { CREATE_NEW_TABLE_BUTTON_TEXT } from "@zapier/canvas-constants";

import { CreateAssetLoadingButton } from "../CreateAssetLoadingButton";
import { ErrorBoundary } from "../ErrorBoundary";

function SuspendedCreateNewTableButton() {
  const nodeId = useNodeId();
  const setShouldCreateNewTable = useSetAtom(shouldCreateNewTableAtom);
  const createNewTableResponse = useAtomValue(createNewTableAtom_SUSPENDS);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const refreshTables = useSetAtom(tablesAtom_SUSPENDS);
  const { emitEventOnTableCreation } = useEmitResourceCreatedEvents();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  const moveAssetToFolder = useSetAtom(moveAssetToCanvasFolderSetter);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const createNewTable = useCallback(() => {
    setShouldCreateNewTable(true);
  }, [setShouldCreateNewTable]);

  // Reset the shouldCreateNewTableAtom to false after the createNewTableResponse completes
  useEffect(() => {
    if (createNewTableResponse !== null) {
      const tableId = createNewTableResponse.id;

      if (tableId) {
        closeSecondaryMenu();

        const updatedNodes = updateNode({
          id: nodeId!,
          data: {
            tableId,
          },
        });

        saveToBackend({ nodes: updatedNodes });

        refreshTables(); // Trigger tables to be re-fetched, so the new table can be populated in the Tables Typeahead
        setShouldCreateNewTable(false);

        emitEventOnTableCreation(tableId);
        moveAssetToFolder({ assetType: "table", assetId: tableId });
      }
    }
  }, [
    createNewTableResponse,
    setShouldCreateNewTable,
    saveToBackend,
    updateNode,
    emitEventOnTableCreation,
    refreshTables,
    moveAssetToFolder,
    nodeId,
    closeSecondaryMenu,
  ]);

  return (
    <>
      <Button
        // @ts-ignore
        ref={buttonRef}
        variant="primary"
        size="compact"
        iconPosition="start"
        onPress={createNewTable}
      >
        <Icon name="formAdd" />
        {CREATE_NEW_TABLE_BUTTON_TEXT}
      </Button>
    </>
  );
}

export function CreateNewTableButton() {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <CreateAssetLoadingButton>
            {CREATE_NEW_TABLE_BUTTON_TEXT}
          </CreateAssetLoadingButton>
        }
      >
        <SuspendedCreateNewTableButton />
      </Suspense>
    </ErrorBoundary>
  );
}
